import type { Config } from '../../../_config'
import { FactoryFetcher } from '../../../_fetcher/FactoryFetcher'
import { DomainError } from '../../../_kernel/DomainError'
import { ErrorsCode } from '../../../_kernel/ErrorsCode'
import type { ID } from '../../../_kernel/ID'
import type { ListOfID } from '../../../_kernel/ListOfID'
import { ListOfPaginatedID } from '../../../_kernel/ListOfPaginatedID'
import { ListOfProperty } from '../../domain/ListOfProperty'
import { ListOfTheme } from '../../domain/ListOfTheme'
import type {
  deleteLikeInput,
  findByIDsInput,
  getBannerPropertiesInput,
  getThemesInput,
  getUserLikesIdsInput,
  isLikedInput,
  PropertyRepository,
  relatedByIDInput,
  saveLikeInput,
} from '../../domain/PropertyRepository'
import type { POSTBannerResponseType } from './schemas/banner'
import { POSTBannerPropertiesBody, POSTBannerResponse } from './schemas/banner'
import { GETpropertiesByIDsQuery, GETpropertiesByIDsResponse } from './schemas/properties/ids'
import type { POSTRelatedResponseType } from './schemas/related'
import { POSTRelatedBody, POSTRelatedResponse } from './schemas/related'
import type { GETLikesPropertiesResponseType } from './schemas/user-engagement/user/likes'
import {
  GETLikesPropertiesQuery,
  GETLikesPropertiesResponse,
} from './schemas/user-engagement/user/likes'

import { LikeBelong } from '../../domain/LikeBelong'
import type { GetUserLikesPropertyResponseType } from './schemas/properties/:id/like'
import { GetUserLikesPropertyResponse } from './schemas/properties/:id/like'
import type { GETThemesResponseType } from './schemas/themes'
import { GETThemesQuery, GETThemesResponse } from './schemas/themes'

export class HTTPPropertyRepository implements PropertyRepository {
  static create({ config }: { config: Config }) {
    return new HTTPPropertyRepository(config)
  }

  constructor(private config: Config) {}

  async saveLastSeenIDs(): Promise<ListOfID> {
    throw DomainError.withCodes(ErrorsCode.FORBIDDEN_REPOSITORY_METHOD)
  }

  async lastSeenIDs(): Promise<ListOfID> {
    throw DomainError.withCodes(ErrorsCode.FORBIDDEN_REPOSITORY_METHOD)
  }

  async findByIDs({ ids, lang }: findByIDsInput) {
    if (ids.isEmpty()) {
      return ListOfProperty.empty()
    }

    const fetcher = await FactoryFetcher.create({ config: this.config }).fetcher()
    const API_GATEWAY = this.config.get('API_GATEWAY')
    const [err, json] = await fetcher.get<GETpropertiesByIDsResponse>(
      `${API_GATEWAY}/v3/properties/ids?id_properties=${ids}&lang=${lang.value}`,
      {
        schemas: { response: GETpropertiesByIDsResponse, query: GETpropertiesByIDsQuery },
      }
    )

    if (err)
      throw DomainError.withCodes(ErrorsCode.FIND_BY_IDS_PROPERTIES_NOT_FOUND).withCauses(err)

    return ListOfProperty.fromAPI({ json })
  }

  async findByBannerID({ id, lang, market, partialSearch, affiliate }: getBannerPropertiesInput) {
    const fetcher = await FactoryFetcher.create({ config: this.config }).fetcher()
    const API_GATEWAY = this.config.get('API_GATEWAY')
    const body = {
      checkin: partialSearch?.checkIn,
      checkout: partialSearch?.checkOut,
      isFamiliar: false,
      rooms: partialSearch?.toJSON().paxes,
      sortBy: 'distance',
      lang: lang.value,
      market: market.value,
    }

    const [err, json] = await fetcher.post<POSTBannerResponseType>(
      `${API_GATEWAY}/v2/banners/${id.value}/properties`,
      {
        schemas: { response: POSTBannerResponse, body: POSTBannerPropertiesBody },
        body: JSON.stringify(body),
      }
    )

    if (err) throw DomainError.withCodes(ErrorsCode.FIND_BY_BANNER_ID_PROPERTIES_NOT_FOUND)

    return ListOfProperty.fromBannerApi({ json, affiliate, market })
  }

  async relatedByID({
    id,
    lang,
    market,
    partialSearch,
    affiliate,
    currency,
    country,
  }: relatedByIDInput) {
    const fetcher = await FactoryFetcher.create({ config: this.config }).fetcher()
    const API_GATEWAY = this.config.get('API_GATEWAY')
    const body = {
      idProperty: +id.value,
      lang: lang.value,
      market: market.value,
      rooms: partialSearch?.toJSON().paxes,
      checkin: partialSearch?.checkIn,
      checkout: partialSearch?.checkOut,
      affiliate: affiliate?.value,
      country: country.value,
      currency: currency.value,
      source: 'web',
    }

    const [err, json] = await fetcher
      .withAuthMapper(url => `${url}/member`)
      .post<POSTRelatedResponseType>(`${API_GATEWAY}/v3/related`, {
        schemas: { response: POSTRelatedResponse, body: POSTRelatedBody },
        body: JSON.stringify(body),
      })

    if (err) throw DomainError.withCodes(ErrorsCode.RELATED_PROPERTIES_NOT_FOUND).withCauses(err)

    return ListOfProperty.fromRelatedAPI({ json, affiliate, market })
  }

  async saveLike({ id }: saveLikeInput): Promise<ID> {
    const fetcher = await FactoryFetcher.create({ config: this.config }).fetcher()

    const API_GATEWAY = this.config.get('API_GATEWAY')
    const [err] = await fetcher
      .withAuth()
      .post(`${API_GATEWAY}/user-engagement/properties/${id.value}/like`)

    if (err) throw DomainError.withCodes(ErrorsCode.ERROR_SAVING_PROPERTY_LIKE).withCauses(err)

    return id
  }

  async deleteLike({ id }: deleteLikeInput): Promise<ID> {
    const fetcher = await FactoryFetcher.create({ config: this.config }).fetcher()

    const API_GATEWAY = this.config.get('API_GATEWAY')
    const [err] = await fetcher
      .withAuth()
      .del(`${API_GATEWAY}/user-engagement/properties/${id.value}/like`)

    if (err) throw DomainError.withCodes(ErrorsCode.ERROR_DELETING_PROPERTY_LIKE).withCauses(err)

    return id
  }

  async isLiked({ id }: isLikedInput): Promise<LikeBelong> {
    const fetcher = await FactoryFetcher.create({ config: this.config }).fetcher()

    const API_GATEWAY = this.config.get('API_GATEWAY')
    const [err, json] = await fetcher
      .withAuth()
      .get<GetUserLikesPropertyResponseType>(
        `${API_GATEWAY}/user-engagement/properties/${id.value}/like`,
        {
          schemas: { response: GetUserLikesPropertyResponse },
        }
      )

    if (err) throw DomainError.withCodes(ErrorsCode.ERROR_GETTING_PROPERTY_LIKE).withCauses(err)

    if (!json) return LikeBelong.create({ value: false })

    return LikeBelong.create({ value: true })
  }

  async getUserLikesIds({ pagination }: getUserLikesIdsInput): Promise<ListOfPaginatedID> {
    const fetcher = await FactoryFetcher.create({ config: this.config }).fetcher()
    const API_GATEWAY = this.config.get('API_GATEWAY')

    const [err, json] = await fetcher
      .withAuth()
      .get<GETLikesPropertiesResponseType>(
        `${API_GATEWAY}/user-engagement/user/likes?page=${pagination.offset}&limit=${pagination.limit}`,
        {
          schemas: { response: GETLikesPropertiesResponse, query: GETLikesPropertiesQuery },
        }
      )

    if (err) throw DomainError.withCodes(ErrorsCode.ERROR_GETTING_PROPERTY_LIKES).withCauses(err)

    if (!json) return ListOfPaginatedID.empty()

    return ListOfPaginatedID.fromLikesAPI({ json })
  }

  async getThemes({ lang }: getThemesInput): Promise<ListOfTheme> {
    const fetcher = await FactoryFetcher.create({ config: this.config }).fetcher()
    const API_GATEWAY = this.config.get('API_GATEWAY')

    const [err, json] = await fetcher.get<GETThemesResponseType>(
      `${API_GATEWAY}/v1/themes?language=${lang.value}`,
      {
        schemas: { response: GETThemesResponse, query: GETThemesQuery },
      }
    )

    if (err) {
      throw DomainError.withCodes(ErrorsCode.ERROR_GETTING_THEMES).withCauses(err)
    }

    if (!json || !json.themes || json.themes.length === 0) {
      return ListOfTheme.empty()
    }

    return ListOfTheme.fromAPI({ json })
  }
}
